import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getStoreId, getStoreIdFromAuth } from '../utils/auth';
import * as logger from '../utils/logger';
import { showToast } from '../utils/toast';
import Loading from './Loading';
import ErrorMessage from './ErrorMessage';

const llmsTxtEnabled = (() => {
  const v = import.meta.env.VITE_LLMS_TXT_ENABLED;
  if (v === undefined || v === '') return true;
  const n = String(v).toLowerCase();
  return n === 'true' || n === '1';
})();

const indexNowEnabled = (() => {
  const v = import.meta.env.VITE_INDEXNOW_ENABLED;
  if (v === undefined || v === '') return true;
  const n = String(v).toLowerCase();
  return n === 'true' || n === '1';
})();

/** Show LLMs.txt + IndexNow upgrade marketing when either feature flag is on. */
const aiDiscoveryUpgradeEnabled = llmsTxtEnabled || indexNowEnabled;

const Upgrade = () => {
  const { t } = useTranslation();
  const apiUrl = import.meta.env.VITE_BACKEND_URL;
  const hashSiteId = getStoreId();
  
  const [selectedProducts, setSelectedProducts] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  // const [paypalClientId, setPaypalClientId] = useState('');
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [paypalLoaded, setPaypalLoaded] = useState(false);
  // const [paypalInitialized, setPaypalInitialized] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [productCount, setProductCount] = useState(null);
  const [productCountLoading, setProductCountLoading] = useState(false);
  // const [paymentMethod, setPaymentMethod] = useState('paypal'); // 'paypal' | 'ecwid'
  const [ecwidChargeLoading, setEcwidChargeLoading] = useState(false);
  const [ecwidChargeError, setEcwidChargeError] = useState(null);
  const [noPaymentOption, setNoPaymentOption] = useState(null);
  const [continueLoading, setContinueLoading] = useState(false);
  const [aiDiscovery, setAiDiscovery] = useState(null);
  const [aidBillingCycle, setAidBillingCycle] = useState('MONTHLY');
  const [allCatalogPlans, setAllCatalogPlans] = useState([]);
  const [showAidPayment, setShowAidPayment] = useState(false);
  const [selectedAidPlan, setSelectedAidPlan] = useState(null);
  const [aidChargeLoading, setAidChargeLoading] = useState(false);
  const [cancelProductLine, setCancelProductLine] = useState('regular');
  const [keywordPack, setKeywordPack] = useState(null);
  const [selectedKeywordPackSku, setSelectedKeywordPackSku] = useState('');
  const [kwChargeLoading, setKwChargeLoading] = useState(false);

  const isAidSku = (sku) => {
    const normalized = String(sku || '').toUpperCase();
    return normalized.startsWith('ECWID-AID') || normalized.startsWith('BIGC-AID') || normalized.startsWith('SEO-AID');
  };

  const isKwSku = (sku) => {
    const normalized = String(sku || '').toUpperCase();
    return normalized.startsWith('SEO-KW') || normalized.startsWith('ECWID-KW');
  };

  const FALLBACK_KEYWORD_PACKS = [
    { sku: 'SEO-KW-25', code: 'SEO-KW-25', subscription_type: 'MONTHLY', no_of_keywords: 25, discounted_price: '2.99', actual_price: '2.99' },
    { sku: 'SEO-KW-50', code: 'SEO-KW-50', subscription_type: 'MONTHLY', no_of_keywords: 50, discounted_price: '4.99', actual_price: '4.99' },
    { sku: 'SEO-KW-100', code: 'SEO-KW-100', subscription_type: 'MONTHLY', no_of_keywords: 100, discounted_price: '9.99', actual_price: '8.99' },
    { sku: 'SEO-KW-250', code: 'SEO-KW-250', subscription_type: 'MONTHLY', no_of_keywords: 250, discounted_price: '19.99', actual_price: '14.99' },
    { sku: 'SEO-KW-500', code: 'SEO-KW-500', subscription_type: 'MONTHLY', no_of_keywords: 500, discounted_price: '34.99', actual_price: '24.99' },
  ];

  const keywordPackPlansFromCatalog = allCatalogPlans
    .filter((s) => isKwSku(s.sku || s.code) && String(s.subscription_type || '').toUpperCase() === 'MONTHLY')
    .sort((a, b) => (parseInt(a.no_of_keywords, 10) || 0) - (parseInt(b.no_of_keywords, 10) || 0));

  const keywordPackPlans = keywordPackPlansFromCatalog.length > 0
    ? keywordPackPlansFromCatalog
    : FALLBACK_KEYWORD_PACKS;

  const selectedKeywordPackPlan = keywordPackPlans.find(
    (s) => String(s.sku || s.code) === selectedKeywordPackSku
  ) || null;

  const getKeywordPackPrice = () => {
    if (!selectedKeywordPackPlan) return 0;
    return parseFloat(selectedKeywordPackPlan.discounted_price) || 0;
  };

  const aidPlanForCycle = (cycle) => {
    const preferred = cycle === 'YEARLY' ? ['ECWID-AID-Y', 'SEO-AID-Y'] : ['ECWID-AID-M', 'SEO-AID-M'];
    for (const code of preferred) {
      const found = allCatalogPlans.find((s) => String(s.sku || s.code || '').toUpperCase() === code);
      if (found) return found;
    }
    return allCatalogPlans.find(
      (s) => isAidSku(s.sku || s.code) && String(s.subscription_type || '').toUpperCase() === cycle
    ) || null;
  };

  const selectedAidCatalogPlan = aidPlanForCycle(aidBillingCycle);
  const aidPrice = selectedAidCatalogPlan?.discounted_price || (aidBillingCycle === 'YEARLY' ? '49.99' : '4.99');
  const aidActualPrice = selectedAidCatalogPlan?.actual_price || (aidBillingCycle === 'YEARLY' ? '59.99' : '4.99');
  const aidYearlyDiscountPct = (() => {
    const yearly = aidPlanForCycle('YEARLY');
    if (!yearly) return 17;
    const actual = parseFloat(yearly.actual_price);
    const discounted = parseFloat(yearly.discounted_price);
    if (!Number.isFinite(actual) || actual <= 0 || !Number.isFinite(discounted)) return 17;
    return Math.round(((actual - discounted) / actual) * 100);
  })();
  const hasActivePlan = !!(currentSubscription && currentSubscription.status !== 'SUSPENDED');
  const hasActiveAiDiscovery = !!(aiDiscovery?.active && !hasActivePlan);

  // Fetch enabled (storefront-active) product count via Ecwid Search products API
  // Docs: enabled=true → only enabled products; total is the matching count
  // https://docs.ecwid.com/api-reference/rest-api/products/search-products
  const fetchProductCount = async () => {
    if (!hashSiteId) return;
    
    setProductCountLoading(true);
    try {
      // Backend maps onlineOnly=true → Ecwid params.enabled = true
      const response = await fetch(
        `${apiUrl}/api/seo-optimizer/products?id=${hashSiteId}&limit=1&offset=0&onlineOnly=true`
      );
      if (response.ok) {
        const data = await response.json();
        setProductCount(data.total || 0);
      } else {
        logger.warn('Could not fetch product count');
        setProductCount(null);
      }
    } catch (err) {
      logger.log('Product count not available:', err);
      setProductCount(null);
    } finally {
      setProductCountLoading(false);
    }
  };

  const refreshSubscription = async (options = {}) => {
    const { skipToast } = options;
    if (!hashSiteId) return;
    
    setRefreshing(true);
    try {
      const subResponse = await fetch(`${apiUrl}/api/subscription?id=${hashSiteId}`);
      if (subResponse.ok) {
        const subData = await subResponse.json();
        if (subData.subscription) {
          setCurrentSubscription(subData.subscription);
          if (!skipToast) showToast(t('upgrade.subscriptionRefreshed'), 'success');
        } else {
          setCurrentSubscription(null);
          if (!skipToast) showToast(t('upgrade.noActiveSubscription'), 'info');
        }
        setAiDiscovery(subData.ai_discovery || null);
        setKeywordPack(subData.keyword_pack || null);
      } else {
        if (!skipToast) showToast(t('upgrade.refreshFailed'), 'error');
      }
    } catch (error) {
      logger.error('Error refreshing subscription:', error);
      if (!skipToast) showToast(t('upgrade.refreshFailed'), 'error');
    }
    setRefreshing(false);
  };

  // Fetch pricing and subscription in parallel so the page loads as fast as the slower of the two
  useEffect(() => {
    if (!hashSiteId) {
      setLoading(false);
      return;
    }

    const fetchPricing = async () => {
      const response = await fetch(`${apiUrl}/api/pricing?id=${hashSiteId}`);
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch pricing');
      return data;
    };

    const fetchSubscription = async () => {
      const subResponse = await fetch(`${apiUrl}/api/subscription?id=${hashSiteId}`);
      if (!subResponse.ok) return { subscription: null, ai_discovery: null };
      return subResponse.json();
    };

    (async () => {
      try {
        const [data, subPayload, paymentStatus] = await Promise.all([
          fetchPricing(),
          fetchSubscription(),
          fetch(`${apiUrl}/api/ecwid-billing/payment-status?id=${hashSiteId}`).then(r => r.ok ? r.json() : { hasPaymentOption: false }).catch(() => ({ hasPaymentOption: false }))
        ]);

        if (paymentStatus && paymentStatus.hasPaymentOption === false) {
          setNoPaymentOption(true);
        } else {
          setNoPaymentOption(false);
        }

        if (data.subscriptions) {
          setAllCatalogPlans(data.subscriptions);
          const monthlyPlans = data.subscriptions.filter(
            (s) => s.subscription_type === 'MONTHLY' && !isAidSku(s.sku || s.code) && !isKwSku(s.sku || s.code)
          );
          setSubscriptions(monthlyPlans);

          const productSet = new Set();
          const productOpts = [];
          monthlyPlans.forEach(sub => {
            const prodValue = (sub.no_of_products || sub.credits || 1000).toString();
            if (!productSet.has(prodValue)) {
              productSet.add(prodValue);
              productOpts.push({
                value: prodValue,
                label: prodValue,
                price: sub.discounted_price
              });
            }
          });
          setProductOptions(productOpts);
          setSelectedProducts(productOpts[0]?.value || '1000');
        }

        if (subPayload?.subscription) setCurrentSubscription(subPayload.subscription);
        setAiDiscovery(subPayload?.ai_discovery || null);
        setKeywordPack(subPayload?.keyword_pack || null);
        setLoading(false);
      } catch (err) {
        logger.error('Error fetching upgrade data:', err);
        setError(err);
        setLoading(false);
      }
    })();

    fetchProductCount();
  }, [hashSiteId]);

  // Get current price based on selected products
  const getCurrentPrice = () => {
    const productOpt = productOptions.find(opt => opt.value === selectedProducts);
    return productOpt?.price || '800';
  };

  const getProPlusKeywordTotal = () => {
    const pro = parseFloat(getCurrentPrice()) || 0;
    return (pro + getKeywordPackPrice()).toFixed(2);
  };

  // Get current credits based on selected products
  const getCurrentCredits = () => {
    const matchingPlan = subscriptions.find(s => {
      const planProducts = (s.no_of_products || s.credits || 0).toString();
      return planProducts === selectedProducts && s.subscription_type === 'MONTHLY';
    });
    return matchingPlan?.credits || selectedProducts;
  };

  // Keyword tracker limit for selected product tier; pack add-on stacks on top
  const getCurrentKeywordTrackerLimit = () => {
    const matchingPlan = subscriptions.find(s => {
      const planProducts = (s.no_of_products || s.credits || 0).toString();
      return planProducts === selectedProducts && s.subscription_type === 'MONTHLY';
    });
    const planBase = matchingPlan?.keyword_tracker_limit;
    if (planBase != null && planBase >= 0) {
      const packExtra = selectedKeywordPackPlan
        ? (parseInt(selectedKeywordPackPlan.no_of_keywords, 10) || 0)
        : 0;
      return planBase + packExtra;
    }
    if (planBase != null && planBase < 0) return planBase;
    // Fallback: API/subscription limit (already includes active pack when present)
    return currentSubscription?.keyword_tracker_limit ?? -1;
  };

  // Get minimum valid product option based on store product count
  const getMinimumValidProductOption = () => {
    if (!productCount || productCount === 0) return null;
    
    const sortedOptions = [...productOptions].sort((a, b) => parseInt(a.value) - parseInt(b.value));
    const minValidOption = sortedOptions.find(opt => parseInt(opt.value) >= productCount);
    return minValidOption?.value || null;
  };

  // Check if a product option is valid (>= store product count)
  const isProductOptionValid = (productValue) => {
    if (!productCount || productCount === 0) return true;
    return parseInt(productValue) >= productCount;
  };

  // Auto-select minimum valid option when product count is loaded
  useEffect(() => {
    if (productCount && productCount > 0 && productOptions.length > 0) {
      const minValid = getMinimumValidProductOption();
      if (minValid && (!selectedProducts || !isProductOptionValid(selectedProducts))) {
        setSelectedProducts(minValid);
      }
    }
  }, [productCount, productOptions, selectedProducts]);

  const handleCancelSubscription = async () => {
    if (!hashSiteId) return;
    const target = cancelProductLine === 'ai_discovery'
      ? aiDiscovery
      : cancelProductLine === 'keyword_pack'
        ? keywordPack
        : currentSubscription;
    if (!target?.subscription_id) return;
    
    try {
      const response = await fetch(`${apiUrl}/api/subscription/cancel?id=${hashSiteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription_id: target.subscription_id,
          product_line: cancelProductLine,
        })
      });
      
      if (response.ok) {
        setShowCancelModal(false);
        showToast(t('upgrade.cancelSuccess'), 'success');
        await refreshSubscription({ skipToast: true });
      } else {
        const errorData = await response.json();
        showToast(errorData.error || t('upgrade.cancelFailed'), 'error');
      }
    } catch (error) {
      logger.error('Error cancelling subscription:', error);
      showToast(t('upgrade.cancelFailedRetry'), 'error');
    }
  };

  const handleContinueSubscription = async () => {
    if (!hashSiteId || !currentSubscription) return;
    setContinueLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/subscription/continue?id=${hashSiteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subscription_id: currentSubscription.subscription_id,
          subscription_code: currentSubscription.subscription_name || null,
          product_line: 'regular',
        })
      });
      if (response.ok) {
        showToast(t('upgrade.continueSuccess'), 'success');
        await refreshSubscription();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showToast(errorData.error || t('upgrade.continueFailed'), 'error');
      }
    } catch (error) {
      logger.error('Error continuing subscription:', error);
      showToast(t('upgrade.continueFailed'), 'error');
    } finally {
      setContinueLoading(false);
    }
  };

  const handleChoosePlan = () => {
    const matchingPlan = subscriptions.find(s => {
      const planProducts = (s.no_of_products || s.credits || 0).toString();
      return planProducts === selectedProducts && s.subscription_type === 'MONTHLY';
    });
    
    const plan = matchingPlan || {
      id: subscriptions[0]?.id || 1,
      subscription_type: 'MONTHLY',
      code: subscriptions[0]?.code || 'PRO_MONTHLY',
      sku: 'Pro',
      credits: parseInt(selectedProducts) || 1000,
      discounted_price: getCurrentPrice(),
      per_cost: '0',
      no_of_account_users: 1,
      no_of_products: parseInt(selectedProducts) || 1000
    };
    
    setSelectedPlan(plan);
    setShowPayment(true);
    setShowAidPayment(false);
    setSelectedAidPlan(null);
    setEcwidChargeError(null);
  };

  const handleChooseAidPlan = () => {
    const plan = selectedAidCatalogPlan;
    if (!plan) {
      showToast(t('upgrade.aidCatalogMissing'), 'error');
      return;
    }
    setSelectedAidPlan(plan);
    setShowAidPayment(true);
    setShowPayment(false);
    setSelectedPlan(null);
    setEcwidChargeError(null);
  };

  const handleEcwidCharge = async () => {
    if (!hashSiteId || !selectedPlan) return;
    const amount = getProPlusKeywordTotal();
    const kwSku = selectedKeywordPackPlan?.sku || selectedKeywordPackPlan?.code || null;
    const description = kwSku
      ? `SEO Spider Pro - ${selectedProducts} products + Keyword pack ${selectedKeywordPackPlan.no_of_keywords}/month`
      : `SEO Spider Pro - ${selectedProducts} products/month`;
    const idempotencyKey = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
    setEcwidChargeLoading(true);
    setEcwidChargeError(null);
    try {
      const response = await fetch(`${apiUrl}/api/ecwid-billing/charge?id=${hashSiteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: parseFloat(amount) || 0,
          currency: 'USD',
          description,
          idempotencyKey,
          metadata: {
            plan_id: selectedPlan.id,
            product_count: selectedProducts,
            subscription_code: selectedPlan.sku,
            ...(kwSku ? { keyword_pack_sku: kwSku } : {}),
          }
        })
      });
      const data = await response.json();
      if (response.ok) {
        setEcwidChargeError(null);
        showToast(t('upgrade.subscriptionActivated'), 'success');
        setShowPayment(false);
        setSelectedPlan(null);
        await refreshSubscription({ skipToast: true });
      } else {
        setEcwidChargeError(t('upgrade.ecwidBillingPaymentAlert'));
        showToast(data.error || t('upgrade.paymentFailed'), 'error');
      }
    } catch (err) {
      logger.error('Ecwid charge error:', err);
      setEcwidChargeError(t('upgrade.ecwidBillingPaymentAlert'));
      showToast(t('upgrade.paymentFailed'), 'error');
    } finally {
      setEcwidChargeLoading(false);
    }
  };

  const handleKeywordPackCharge = async () => {
    if (!hashSiteId || !selectedKeywordPackPlan || !hasActivePlan) return;
    const amount = getKeywordPackPrice();
    const sku = selectedKeywordPackPlan.sku || selectedKeywordPackPlan.code;
    const description = `SEO Spider Keyword Tracking - ${selectedKeywordPackPlan.no_of_keywords} keywords/month`;
    const idempotencyKey = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
    setKwChargeLoading(true);
    setEcwidChargeError(null);
    try {
      const response = await fetch(`${apiUrl}/api/ecwid-billing/charge?id=${hashSiteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          currency: 'USD',
          description,
          idempotencyKey,
          metadata: {
            plan_id: selectedKeywordPackPlan.id,
            subscription_code: sku,
            product_line: 'keyword_pack',
          }
        })
      });
      const data = await response.json();
      if (response.ok) {
        showToast(t('upgrade.subscriptionActivated'), 'success');
        await refreshSubscription({ skipToast: true });
      } else {
        setEcwidChargeError(t('upgrade.ecwidBillingPaymentAlert'));
        showToast(data.error || t('upgrade.paymentFailed'), 'error');
      }
    } catch (err) {
      logger.error('Keyword pack charge error:', err);
      setEcwidChargeError(t('upgrade.ecwidBillingPaymentAlert'));
      showToast(t('upgrade.paymentFailed'), 'error');
    } finally {
      setKwChargeLoading(false);
    }
  };

  const handleAidCharge = async () => {
    if (!hashSiteId || !selectedAidPlan) return;
    const amount = parseFloat(selectedAidPlan.discounted_price || aidPrice) || 0;
    const cycleLabel = aidBillingCycle === 'YEARLY' ? 'year' : 'month';
    const description = `SEO Spider LLMs.txt + IndexNow - ${cycleLabel}ly`;
    const idempotencyKey = typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });
    setAidChargeLoading(true);
    setEcwidChargeError(null);
    try {
      const response = await fetch(`${apiUrl}/api/ecwid-billing/charge?id=${hashSiteId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount,
          currency: 'USD',
          description,
          idempotencyKey,
          metadata: {
            plan_id: selectedAidPlan.id,
            subscription_code: selectedAidPlan.sku || selectedAidPlan.code,
            product_line: 'ai_discovery',
          }
        })
      });
      const data = await response.json();
      if (response.ok) {
        setEcwidChargeError(null);
        showToast(t('upgrade.subscriptionActivated'), 'success');
        setShowAidPayment(false);
        setSelectedAidPlan(null);
        setAiDiscovery({
          active: true,
          sku: selectedAidPlan.sku || selectedAidPlan.code,
          status: 'ACTIVE',
          price: selectedAidPlan.discounted_price,
          subscription_type: selectedAidPlan.subscription_type,
        });
        await refreshSubscription({ skipToast: true });
      } else {
        setEcwidChargeError(t('upgrade.ecwidBillingPaymentAlert'));
        showToast(data.error || t('upgrade.paymentFailed'), 'error');
      }
    } catch (err) {
      logger.error('AID Ecwid charge error:', err);
      setEcwidChargeError(t('upgrade.ecwidBillingPaymentAlert'));
      showToast(t('upgrade.paymentFailed'), 'error');
    } finally {
      setAidChargeLoading(false);
    }
  };

  // const handlePaypalLoad = () => {
  //   logger.log('PayPal SDK loaded successfully');
  //   if (typeof window !== 'undefined' && window.paypal) {
  //     logger.log('PayPal object available:', !!window.paypal);
  //     setPaypalLoaded(true);
  //     if (showPayment && selectedPlan) {
  //       setTimeout(() => {
  //         initializePaypal();
  //       }, 100);
  //     }
  //   } else {
  //     logger.error('PayPal SDK loaded but paypal object not found');
  //   }
  // };

  // const initializePaypal = () => {
  //   if (typeof window === 'undefined' || !window.paypal || !selectedPlan) {
  //     logger.log('PayPal not ready:', {
  //       hasWindow: typeof window !== 'undefined',
  //       hasPaypal: !!window?.paypal,
  //       hasSelectedPlan: !!selectedPlan
  //     });
  //     return;
  //   }

  //   if (paypalInitialized) {
  //     logger.log('PayPal already initialized, skipping...');
  //     return;
  //   }

  //   logger.log('Initializing PayPal buttons for subscription');

  //   const container = document.getElementById('paypal-button-container-pro');
  //   if (!container) {
  //     logger.error('Container not found: paypal-button-container-pro');
  //     return;
  //   }
  //   container.innerHTML = '';
    
  //   try {
  //     window.paypal.Buttons({
  //       style: {
  //         shape: 'rect',
  //         color: 'gold',
  //         layout: 'vertical',
  //         label: 'subscribe'
  //       },
  //       async createSubscription() {
  //         try {
  //           const response = await fetch(`${apiUrl}/api/paypal/create-subscription?id=${hashSiteId}`, {
  //             method: 'POST',
  //             headers: { 'Content-Type': 'application/json' },
  //             body: JSON.stringify({
  //               planid: selectedPlan.code,
  //               subscription_id: String(selectedPlan.id),
  //               quantity: 1
  //             })
  //           });
  //           const orderData = await response.json();
  //           if (orderData.id) return orderData.id;
  //         } catch (error) {
  //           logger.error(error);
  //         }
  //       },
  //       async onApprove(data) {
  //         try {
  //           const response = await fetch(`${apiUrl}/api/paypal/capture-subscription?id=${hashSiteId}`, {
  //             method: 'POST',
  //             headers: { 'Content-Type': 'application/json' },
  //             body: JSON.stringify({
  //               order_id: data.orderID,
  //               subscription_id: data.subscriptionID,
  //               facilitatorAccessToken: data.facilitatorAccessToken
  //             })
  //           });
  //           const captureData = await response.json();
  //           logger.log('<onApprove> captureData:', captureData);
  //           if (response.status === 200) {
  //             showToast(t('upgrade.subscriptionActivated'), 'success');
  //             setShowPayment(false);
  //             const subResponse = await fetch(`${apiUrl}/api/subscription?id=${hashSiteId}`);
  //             if (subResponse.ok) {
  //               const subData = await subResponse.json();
  //               if (subData.subscription) {
  //                 setCurrentSubscription(subData.subscription);
  //               }
  //             }
  //           } else {
  //             showToast(t('upgrade.paymentFailed'), 'error');
  //           }
  //         } catch (error) {
  //           logger.error(error);
  //         }
  //       }
  //     }).render('#paypal-button-container-pro').then(() => {
  //       logger.log('PayPal subscription button rendered successfully');
  //       setPaypalInitialized(true);
  //     }).catch((err) => {
  //       logger.error('Error rendering PayPal subscription button:', err);
  //     });
  //   } catch (err) {
  //     logger.error('Error initializing PayPal subscription button:', err);
  //   }
  // };

  // // Load PayPal SDK
  // useEffect(() => {
  //   if (!paypalClientId) return;
  //   if (document.getElementById('paypal-sdk-script')) return;
    
  //   const script = document.createElement('script');
  //   script.id = 'paypal-sdk-script';
  //   script.src = `https://www.paypal.com/sdk/js?client-id=${paypalClientId}&vault=true&disable-funding=paylater&currency=USD&components=buttons`;
  //   script.async = true;
  //   script.onload = handlePaypalLoad;
  //   script.onerror = (e) => {
  //     logger.error('Error loading PayPal SDK:', e);
  //     setError(new Error(t('upgrade.failedToLoadPayPal')));
  //   };
  //   document.body.appendChild(script);
    
  //   return () => {
  //     const existingScript = document.getElementById('paypal-sdk-script');
  //     if (existingScript) {
  //       document.body.removeChild(existingScript);
  //     }
  //   };
  // }, [paypalClientId]);

  // // Initialize PayPal when SDK is loaded, payment section is shown, and method is PayPal
  // useEffect(() => {
  //   if (paymentMethod === 'paypal' && paypalLoaded && showPayment && selectedPlan) {
  //     const timer = setTimeout(() => {
  //       initializePaypal();
  //     }, 300);
  //     return () => clearTimeout(timer);
  //   }
  // }, [paymentMethod, paypalLoaded, showPayment, selectedPlan]);

  // useEffect(() => {
  //   if (paymentMethod === 'paypal' && showPayment && selectedPlan && typeof window !== 'undefined' && window.paypal && !paypalLoaded) {
  //     setPaypalLoaded(true);
  //     const timer = setTimeout(() => {
  //       initializePaypal();
  //     }, 300);
  //     return () => clearTimeout(timer);
  //   }
  // }, [paymentMethod, showPayment, selectedPlan]);

  if (loading) return <Loading />;
  if (error) return <ErrorMessage error={error} />;

  // When subscription is suspended, show pricing with selectable plans (same as trial)
  const showSelectablePricing = !currentSubscription || currentSubscription?.status === 'SUSPENDED';

  const billingUrl = getStoreIdFromAuth() ? `https://my.ecwid.com/store/${getStoreIdFromAuth()}#billing` : null;

  return (
    <div style={{ padding: '1.5rem 0 3rem 0' }}>
      {/* No payment option: show error and link to Ecwid billing */}
      {noPaymentOption === true && (
        <div className="container-fluid px-3 mb-3">
          <div className="alert alert-danger d-flex align-items-center" role="alert">
            <span className="me-2">⚠</span>
            <div className="flex-grow-1">
              {t('upgrade.noPaymentOption')}{' '}
              {billingUrl && (
                <a href={billingUrl} target="_blank" rel="noopener noreferrer" className="alert-link fw-bold">
                  {t('upgrade.ecwidBillingPaymentLink')}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      {currentSubscription ? (
        currentSubscription.status === 'SUSPENDED' ? (
          <div className="container-fluid px-3 mb-3">
            <div className="card border border-warning shadow-sm bg-warning bg-opacity-10">
              <div className="card-body py-3 px-3">
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <span className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: 24, height: 24 }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="#fff">
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                      <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
                    </svg>
                  </span>
                  <span className="fw-bold text-dark" style={{ fontSize: '1rem' }}>{t('upgrade.suspendedSubscription')}</span>
                </div>
                <p className="text-dark mb-0 mt-2 mb-1" style={{ fontSize: '0.9rem' }}>
                  {t('upgrade.suspendedMessage', {
                    nextBillingDate: currentSubscription.next_billing_date
                      ? new Date(currentSubscription.next_billing_date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
                      : t('upgrade.na')
                  })}
                </p>
                <div className="d-flex flex-wrap align-items-center gap-3 mt-2" style={{ fontSize: '0.875rem' }}>
                  <span className="text-muted">
                    <span className="text-muted">{t('upgrade.plan')}</span> <strong className="text-dark">{currentSubscription.subscription_name || t('upgrade.proPlan')}</strong>
                  </span>
                  <span className="text-muted">
                    <span className="text-muted">{t('upgrade.price')}</span> <strong className="text-dark">${currentSubscription.price || '0'}{t('upgrade.perMonth')}</strong>
                  </span>
                  <span className="text-muted text-nowrap" title={currentSubscription.subscription_id}>
                    <span className="text-muted">{t('upgrade.id')}</span> {String(currentSubscription.subscription_id).slice(0, 12)}{String(currentSubscription.subscription_id).length > 12 ? '…' : ''}
                  </span>
                </div>
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleContinueSubscription}
                    disabled={continueLoading}
                  >
                    {continueLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        {t('upgrade.processing')}
                      </>
                    ) : (
                      t('upgrade.iWantToContinue')
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="container-fluid px-3 mb-3">
            <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '12px', borderLeft: '4px solid #198754' }}>
              <div className="card-body p-0">
                <div className="d-flex flex-column flex-md-row align-items-stretch">
                  <div className="d-flex align-items-center px-4 py-3" style={{ background: 'linear-gradient(135deg, #19875408 0%, #19875414 100%)', minWidth: '200px' }}>
                    <span className="bg-success bg-opacity-10 text-success rounded-3 d-flex align-items-center justify-content-center me-3" style={{ width: 48, height: 48 }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M5 20v-2h14v2zm0-3.5L3.725 8.475q-.05 0-.113.013T3.5 8.5q-.625 0-1.062-.438T2 7t.438-1.062T3.5 5.5t1.063.438T5 7q0 .175-.038.325t-.087.275L8 9l3.125-4.275q-.275-.2-.45-.525t-.175-.7q0-.625.438-1.063T12 2t1.063.438T13.5 3.5q0 .375-.175.7t-.45.525L16 9l3.125-1.4q-.05-.125-.088-.275T19 7q0-.625.438-1.063T20.5 5.5t1.063.438T22 7t-.437 1.063T20.5 8.5q-.05 0-.112-.012t-.113-.013L19 16.5z" stroke-width="0.5" stroke="currentColor"/></svg>
                    </span>
                    <div>
                      <div className="text-success text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>{t('upgrade.activeSubscription')}</div>
                      <div className="fw-bold text-dark" style={{ fontSize: '1.1rem' }}>{currentSubscription.subscription_name || t('upgrade.proPlan')}</div>
                    </div>
                  </div>
                  <div className="flex-grow-1 px-4 py-3 d-flex flex-wrap align-items-center gap-4 gap-md-3">
                    <div className="d-flex align-items-baseline gap-1">
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>{t('upgrade.price')}</span>
                      <span className="fw-bold text-dark" style={{ fontSize: '1.25rem' }}>${currentSubscription.price || '0'}</span>
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>{t('upgrade.perMonth')}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>{t('upgrade.products')}</span>
                      <span className="fw-semibold text-dark">{currentSubscription.product_count || '0'}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>{t('upgrade.nextBilling')}</span>
                      <span className="text-dark">
                        {currentSubscription.next_billing_date
                          ? new Date(currentSubscription.next_billing_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                          : t('upgrade.na')}
                      </span>
                    </div>
                  </div>
                  <div className="px-4 py-3 d-flex align-items-center justify-content-between justify-content-md-end gap-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)', background: 'rgba(0,0,0,0.02)' }}>
                    <span className="text-muted text-nowrap" style={{ fontSize: '0.75rem' }} title={currentSubscription.subscription_id}>
                      {t('upgrade.id')} {String(currentSubscription.subscription_id).slice(0, 14)}{String(currentSubscription.subscription_id).length > 14 ? '…' : ''}
                    </span>
                    {currentSubscription.subscription_id !== 'ecwid_billing' && (
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => {
                          setCancelProductLine('regular');
                          setShowCancelModal(true);
                        }}
                      >
                        {t('upgrade.cancelSubscription')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      ) : hasActiveAiDiscovery ? (
          <div className="container-fluid px-3 mb-3">
            <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '12px', borderLeft: '4px solid #0f766e' }}>
              <div className="card-body p-0">
                <div className="d-flex flex-column flex-md-row align-items-stretch">
                  <div className="d-flex align-items-center px-4 py-3" style={{ background: 'linear-gradient(135deg, #0f766e08 0%, #0f766e14 100%)', minWidth: '200px' }}>
                    <div>
                      <div className="text-uppercase fw-semibold" style={{ fontSize: '0.7rem', letterSpacing: '0.05em', color: '#0f766e' }}>{t('upgrade.activeSubscription')}</div>
                      <div className="fw-bold text-dark" style={{ fontSize: '1.1rem' }}>{t('upgrade.llmsIndexNow')}</div>
                    </div>
                  </div>
                  <div className="flex-grow-1 px-4 py-3 d-flex flex-wrap align-items-center gap-4 gap-md-3">
                    {aiDiscovery?.price != null && (
                      <div className="d-flex align-items-baseline gap-1">
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>{t('upgrade.price')}</span>
                        <span className="fw-bold text-dark" style={{ fontSize: '1.25rem' }}>${aiDiscovery.price}</span>
                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                          /{String(aiDiscovery.subscription_type || '').toUpperCase() === 'YEARLY' ? t('upgrade.perYearShort') : t('upgrade.perMonthShort')}
                        </span>
                      </div>
                    )}
                    {aiDiscovery?.next_billing_date && (
                      <div className="d-flex align-items-baseline gap-1">
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>{t('upgrade.nextBilling')}</span>
                        <span className="fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>
                          {new Date(aiDiscovery.next_billing_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-3 d-flex align-items-center justify-content-md-end">
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => {
                        setCancelProductLine('ai_discovery');
                        setShowCancelModal(true);
                      }}
                    >
                      {t('upgrade.cancelSubscription')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
      ) : (
        <div></div>
      )}      

      {/* Cancel Confirmation Modal */}
      {showCancelModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('upgrade.cancelSubscription')}</h5>
                <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>{t('upgrade.cancelConfirmMessage')}</p>
                <p className="text-muted small">{t('upgrade.cancelConfirmHint')}</p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCancelModal(false)}>
                  {t('upgrade.keepSubscription')}
                </button>
                <button type="button" className="btn btn-danger" onClick={handleCancelSubscription}>
                  {t('upgrade.yesCancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* {!paypalClientId && !loading && (
        <div className="alert alert-warning mb-3" style={{ fontSize: '0.875rem' }}>
          <strong>Warning:</strong> PayPal client ID not configured. Please set PAYPAL_CLIENT_ID environment variable.
        </div>
      )} */}

      <div className="container-fluid px-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h2 className="fw-bold mb-0" style={{ fontSize: '1.5rem' }}>{t('upgrade.pricingPlans')}</h2>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm"
            onClick={refreshSubscription}
            disabled={refreshing}
          >
            {refreshing ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('upgrade.refreshing')}
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" className="me-2">
                  <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                  <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
                </svg>
                {t('upgrade.refresh')}
              </>
            )}
          </button>
        </div>
        <p className="text-center text-muted mb-1" style={{ fontSize: '0.9rem' }}>
          {t(aiDiscoveryUpgradeEnabled ? 'upgrade.choosePlanIntro' : 'upgrade.choosePlanIntroNoAi')}
        </p>
        {aiDiscoveryUpgradeEnabled ? (
          <p className="text-center text-muted mb-3" style={{ fontSize: '0.8rem' }}>
            {t('upgrade.choosePlanIntroSecondary')}
          </p>
        ) : (
          <div className="mb-3" />
        )}

        {/* Main SEO suite: Trial + Pro */}
        <div className="row g-3 mb-3 align-items-stretch">
          {showSelectablePricing && (
            <div className="col-12 col-lg-6">
              <div
                className="card h-100 position-relative"
                style={{
                  borderRadius: '0.75rem',
                  borderWidth: '1px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}
              >
                <div className="card-body p-3 d-flex flex-column">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                      style={{ width: '32px', height: '32px', background: '#6c757d20', color: '#6c757d' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                      </svg>
                    </div>
                    <h5 className="mb-0 fw-bold">{t('upgrade.trial')}</h5>
                  </div>
                  <div className="mb-1">
                    <span className="fw-bold" style={{ fontSize: '1.75rem', lineHeight: 1.1 }}>$0</span>
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>{t('upgrade.perMonth')}</span>
                  </div>
                  <p className="text-muted small mb-2" style={{ fontSize: '0.8rem' }}>{t('upgrade.trialSubtitle')}</p>
                  <button type="button" className="btn btn-secondary btn-sm w-100 mb-2" disabled>
                    {t('upgrade.currentPlan')}
                  </button>
                  <div className="d-flex flex-column gap-1 mt-1">
                    {[
                      t('upgrade.freeProductSeoAudits'),
                      t('upgrade.freeImageOptimizations'),
                      t('upgrade.freeBulkOptimizations'),
                      t('upgrade.freeAltTextGenerations'),
                      t('upgrade.keywordTrackingLimit', { count: 2 }),
                      t('upgrade.freeBulkProductEditor'),
                      t('upgrade.basicSeoReports'),
                      t('upgrade.analytics'),
                      t('upgrade.urlEditor'),
                    ].map((label) => (
                      <div key={label} className="d-flex align-items-center gap-2" style={{ fontSize: '0.8rem', lineHeight: 1.35 }}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="#22c55e" style={{ flexShrink: 0 }}>
                          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                        </svg>
                        <span>{label}</span>
                      </div>
                    ))}
                    <div className="d-flex align-items-center gap-2" style={{ color: '#9ca3af', fontSize: '0.8rem', lineHeight: 1.35 }}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="#e5e7eb" style={{ flexShrink: 0 }}>
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                      </svg>
                      <span style={{ textDecoration: 'line-through' }}>{t('upgrade.cruiseControl')}</span>
                    </div>
                    {aiDiscoveryUpgradeEnabled ? (
                      <div className="d-flex align-items-center gap-2" style={{ color: '#9ca3af', fontSize: '0.8rem', lineHeight: 1.35 }}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="#e5e7eb" style={{ flexShrink: 0 }}>
                          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                        </svg>
                        <span style={{ textDecoration: 'line-through' }}>{t('upgrade.llmsIndexNow')}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={`col-12 ${showSelectablePricing ? 'col-lg-6' : 'col-lg-8 mx-auto'}`}>
            <div
              className={`card h-100 position-relative ${showSelectablePricing ? 'border-primary' : ''}`}
              style={{
                borderRadius: '0.75rem',
                borderWidth: showSelectablePricing ? '2px' : '1px',
                boxShadow: showSelectablePricing ? '0 4px 12px rgba(12, 44, 85, 0.2)' : '0 1px 3px rgba(0,0,0,0.08)'
              }}
            >
              <div className="card-body p-3 d-flex flex-column">
                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                  <div
                    className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{ width: '32px', height: '32px', background: 'var(--app-primary-light)', color: 'var(--app-primary)' }}
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M11.251.068a.5.5 0 0 1 .227.58L9.677 6.5H13a.5.5 0 0 1 .364.843l-8 8.5a.5.5 0 0 1-.842-.49L6.323 9.5H3a.5.5 0 0 1-.364-.843l8-8.5a.5.5 0 0 1 .615-.09z"/>
                    </svg>
                  </div>
                  <h5 className="mb-0 fw-bold">{t('upgrade.pro')}</h5>
                  {aiDiscoveryUpgradeEnabled ? (
                    <span className="badge rounded-pill" style={{ background: '#ccfbf1', color: '#0f766e', fontSize: '0.68rem', fontWeight: 600 }}>
                      {t('upgrade.includesLlmsBadge')}
                    </span>
                  ) : null}
                </div>
                <div className="mb-1">
                  <span className="fw-bold" style={{ fontSize: '1.75rem', lineHeight: 1.1 }}>${getCurrentPrice()}</span>
                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>{t('upgrade.perMonth')}</span>
                </div>
                <p className="text-muted small mb-2" style={{ fontSize: '0.8rem' }}>
                  {productCountLoading
                    ? t('upgrade.loadingStoreSize')
                    : productCount != null && productCount > 0
                      ? t(
                          aiDiscoveryUpgradeEnabled
                            ? 'upgrade.proSubtitleWithCount'
                            : 'upgrade.proSubtitleWithCountNoAi',
                          { count: productCount.toLocaleString() },
                        )
                      : t(
                          aiDiscoveryUpgradeEnabled
                            ? 'upgrade.proSubtitle'
                            : 'upgrade.proSubtitleNoAi',
                        )}
                </p>

                {showSelectablePricing && (
                  <div className="mb-2 p-2 bg-light rounded" style={{ border: '1px solid #e5e7eb' }}>
                    <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.8rem' }}>
                      {t('upgrade.numberOfProducts')}
                    </label>
                    <select
                      className="form-select form-select-sm"
                      value={selectedProducts}
                      onChange={(e) => {
                        if (isProductOptionValid(e.target.value)) setSelectedProducts(e.target.value);
                      }}
                    >
                      {productOptions.map(opt => {
                        const isValid = isProductOptionValid(opt.value);
                        return (
                          <option key={opt.value} value={opt.value} disabled={!isValid} style={!isValid ? { color: '#adb5bd', fontStyle: 'italic' } : {}}>
                            {opt.label} {t('upgrade.productsSuffix')}{!isValid ? ` ${t('upgrade.insufficientForStore')}` : ''}
                          </option>
                        );
                      })}
                    </select>
                    {productCount && productCount > 0 && (
                      <div className="mt-2 small text-muted" style={{ fontSize: '0.75rem' }}>
                        {t('upgrade.yourStoreHasBefore')} <strong>{productCount.toLocaleString()}</strong> {t('upgrade.yourStoreHasAfter')}{' '}
                        {getMinimumValidProductOption() && (
                          <> {t('upgrade.minimumPlan', { label: productOptions.find(opt => opt.value === getMinimumValidProductOption())?.label })}</>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <button
                  type="button"
                  className={`btn btn-sm ${currentSubscription && currentSubscription.status !== 'SUSPENDED' ? 'btn-success' : 'btn-primary'} w-100 mb-2`}
                  onClick={handleChoosePlan}
                  disabled={!showSelectablePricing}
                >
                  {currentSubscription && currentSubscription.status !== 'SUSPENDED' ? t('upgrade.activePlan') : t('upgrade.choosePro')}
                </button>

                {aiDiscoveryUpgradeEnabled ? (
                  <div
                    className="d-flex align-items-center gap-2 mb-2 px-2 py-1"
                    style={{ fontSize: '0.8rem', lineHeight: 1.35, background: '#f0fdfa', borderRadius: '0.4rem', border: '1px solid #99f6e4' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="#0f766e" style={{ flexShrink: 0 }}>
                      <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
                    </svg>
                    <span style={{ color: '#0f766e', fontWeight: 600 }}>{t('upgrade.llmsIncludedCallout')}</span>
                  </div>
                ) : null}

                <div className="d-flex flex-column gap-1 mt-1">
                  {[
                    t('upgrade.unlimitedSeoAudits'),
                    t('upgrade.unlimitedImageOptimizations'),
                    t('upgrade.unlimitedBulkOptimizations'),
                    `${getCurrentCredits()} ${t('upgrade.aiEditsAltText')}`,
                    (() => {
                      const limit = getCurrentKeywordTrackerLimit();
                      return limit != null && limit >= 0
                        ? t('upgrade.keywordTrackingLimit', { count: limit })
                        : t('upgrade.unlimitedKeywordTracking');
                    })(),
                    t('upgrade.pageInsights'),
                    t('upgrade.unlimitedBulkProductEditor'),
                    t('upgrade.cruiseControl'),
                    t('upgrade.analytics'),
                    t('upgrade.priorityEmailSupport'),
                    t('upgrade.liveChatSupport'),
                  ].map((label) => (
                    <div key={label} className="d-flex align-items-center gap-2" style={{ fontSize: '0.8rem', lineHeight: 1.35 }}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="#22c55e" style={{ flexShrink: 0 }}>
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                      </svg>
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-2 mt-1">
          <div className="d-flex align-items-center gap-2 mb-1">
            <hr className="flex-grow-1 my-0" style={{ opacity: 0.2 }} />
            <span className="text-muted text-uppercase flex-shrink-0" style={{ fontSize: '0.7rem', letterSpacing: '0.06em', fontWeight: 600 }}>
              {t('upgrade.standaloneOption')}
            </span>
            <hr className="flex-grow-1 my-0" style={{ opacity: 0.2 }} />
          </div>
          <p className="text-center text-muted mb-2" style={{ fontSize: '0.8rem' }}>
            {t(
              aiDiscoveryUpgradeEnabled
                ? 'upgrade.standaloneRowIntro'
                : 'upgrade.standaloneRowIntroNoAi',
            )}
          </p>
        </div>

        <div className="row g-3 mb-3 align-items-stretch">
          <div className="col-12 col-lg-6">
            <div className="card h-100 position-relative" style={{ borderRadius: '0.75rem', borderWidth: '1px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <div className="card-body p-3 d-flex flex-column">
                <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                  <h5 className="mb-0 fw-bold" style={{ fontSize: '1.05rem' }}>{t('upgrade.keywordPackTitle')}</h5>
                  <span className="badge rounded-pill" style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: '0.65rem', fontWeight: 600 }}>
                    {t('upgrade.proAddOn')}
                  </span>
                </div>
                <p className="text-muted small mb-2" style={{ fontSize: '0.8rem' }}>{t('upgrade.keywordPackSubtitle')}</p>

                {keywordPack?.active ? (
                  <>
                    <button type="button" className="btn btn-secondary btn-sm w-100 mb-2" disabled>
                      {t('upgrade.currentPack', { count: keywordPack.limit })}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm w-100 mb-2"
                      onClick={() => {
                        setCancelProductLine('keyword_pack');
                        setShowCancelModal(true);
                      }}
                    >
                      {t('upgrade.cancelKeywordPack')}
                    </button>
                  </>
                ) : (
                  <>
                    <label className="form-label fw-semibold mb-1" style={{ fontSize: '0.8rem' }}>
                      {t('upgrade.selectKeywordPack')}
                    </label>
                    <select
                      className="form-select form-select-sm mb-2"
                      value={selectedKeywordPackSku}
                      onChange={(e) => setSelectedKeywordPackSku(e.target.value)}
                      disabled={!showSelectablePricing && !hasActivePlan}
                    >
                      <option value="">{t('upgrade.noKeywordPack')}</option>
                      {keywordPackPlans.map((plan) => (
                        <option key={plan.sku || plan.code} value={plan.sku || plan.code}>
                          {t('upgrade.keywordPackOption', {
                            count: plan.no_of_keywords,
                            price: plan.discounted_price,
                          })}
                        </option>
                      ))}
                    </select>
                    {!hasActivePlan && showSelectablePricing ? (
                      <p className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                        {t('upgrade.keywordPackWithProHint')}
                      </p>
                    ) : null}
                    {!hasActivePlan && !showSelectablePricing ? (
                      <p className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                        {t('upgrade.keywordPackRequiresPro')}
                      </p>
                    ) : null}
                    {hasActivePlan && selectedKeywordPackPlan ? (
                      <button
                        type="button"
                        className="btn btn-primary btn-sm w-100 mb-2"
                        onClick={handleKeywordPackCharge}
                        disabled={kwChargeLoading || noPaymentOption === true}
                      >
                        {kwChargeLoading ? t('upgrade.processing') : t('upgrade.addKeywordPack', {
                          price: selectedKeywordPackPlan.discounted_price,
                        })}
                      </button>
                    ) : null}
                    {keywordPackPlansFromCatalog.length === 0 ? (
                      <p className="text-warning mb-0" style={{ fontSize: '0.7rem' }}>
                        {t('upgrade.keywordPackCatalogMissing')}
                      </p>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>

          {aiDiscoveryUpgradeEnabled ? (
            <div className="col-12 col-lg-6">
              <div className="card h-100 position-relative" style={{ borderRadius: '0.75rem', borderWidth: '1px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <div className="card-body p-3 d-flex flex-column">
                  <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
                    <div
                      className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                      style={{ width: '32px', height: '32px', background: '#0f766e20', color: '#0f766e' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M3.612 15.443c-.386.198-.824-.149-.746-.592l.83-4.73L.173 6.765c-.329-.314-.158-.888.283-.95l4.898-.696L7.538.792c.197-.39.73-.39.927 0l2.184 4.327 4.898.696c.441.062.612.636.282.95l-3.522 3.356.83 4.73c.078.443-.36.79-.746.592L8 13.187l-4.389 2.256z"/>
                      </svg>
                    </div>
                    <h5 className="mb-0 fw-bold" style={{ fontSize: '1.05rem' }}>{t('upgrade.llmsIndexNow')}</h5>
                    <span className="badge rounded-pill" style={{ background: '#f1f5f9', color: '#475569', fontSize: '0.65rem', fontWeight: 600 }}>
                      {t('upgrade.aiDiscoveryOnly')}
                    </span>
                  </div>
                  <div className="mb-1 d-flex align-items-center gap-2 flex-wrap">
                    <div>
                      <span className="fw-bold" style={{ fontSize: '1.75rem', lineHeight: 1.1 }}>${aidPrice}</span>
                      <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                        /{aidBillingCycle === 'YEARLY' ? t('upgrade.perYearShort') : t('upgrade.perMonthShort')}
                      </span>
                    </div>
                    {aidBillingCycle === 'YEARLY' && aidYearlyDiscountPct > 0 ? (
                      <span className="badge rounded-pill" style={{ background: '#dcfce7', color: '#166534', fontSize: '0.7rem', fontWeight: 600 }}>
                        {t('upgrade.savePercent', { pct: aidYearlyDiscountPct })}
                      </span>
                    ) : null}
                  </div>
                  <p className="text-muted small mb-2" style={{ fontSize: '0.8rem' }}>{t('upgrade.llmsStandaloneSubtitle')}</p>

                  {!hasActiveAiDiscovery && !hasActivePlan && (
                    <div className="btn-group w-100 mb-2" role="group" aria-label="Billing cycle">
                      <button
                        type="button"
                        className={`btn btn-sm ${aidBillingCycle === 'MONTHLY' ? 'btn-dark' : 'btn-outline-secondary'}`}
                        onClick={() => {
                          setAidBillingCycle('MONTHLY');
                          setShowAidPayment(false);
                          setSelectedAidPlan(null);
                        }}
                      >
                        {t('upgrade.monthly')}
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${aidBillingCycle === 'YEARLY' ? 'btn-dark' : 'btn-outline-secondary'} d-inline-flex align-items-center justify-content-center gap-1`}
                        onClick={() => {
                          setAidBillingCycle('YEARLY');
                          setShowAidPayment(false);
                          setSelectedAidPlan(null);
                        }}
                      >
                        {t('upgrade.yearly')}
                        {aidYearlyDiscountPct > 0 ? (
                          <span
                            className="badge rounded-pill"
                            style={{
                              background: aidBillingCycle === 'YEARLY' ? '#bbf7d0' : '#dcfce7',
                              color: '#166534',
                              fontSize: '0.65rem',
                              fontWeight: 600,
                            }}
                          >
                            -{aidYearlyDiscountPct}%
                          </span>
                        ) : null}
                      </button>
                    </div>
                  )}

                  {hasActivePlan ? (
                    <button type="button" className="btn btn-secondary btn-sm w-100 mb-2" disabled>
                      {t('upgrade.includedWithPro')}
                    </button>
                  ) : hasActiveAiDiscovery ? (
                    <button type="button" className="btn btn-secondary btn-sm w-100 mb-2" disabled>
                      {t('upgrade.currentPlan')}
                    </button>
                  ) : (
                    <button type="button" className="btn btn-dark btn-sm w-100 mb-2" onClick={handleChooseAidPlan}>
                      {t('upgrade.chooseAiDiscovery')}
                    </button>
                  )}
                  {!selectedAidCatalogPlan && !hasActivePlan && !hasActiveAiDiscovery ? (
                    <p className="text-warning mb-2" style={{ fontSize: '0.7rem', lineHeight: 1.3 }}>
                      {t('upgrade.aidCatalogMissing')}
                    </p>
                  ) : null}

                  <div className="d-flex flex-column gap-1 mt-1">
                    {llmsTxtEnabled ? (
                      <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.8rem', lineHeight: 1.35 }}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="#22c55e" style={{ flexShrink: 0 }}>
                          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                        </svg>
                        <span>{t('upgrade.llmsPublishFeature')}</span>
                      </div>
                    ) : null}
                    {indexNowEnabled ? (
                      <div className="d-flex align-items-center gap-2" style={{ fontSize: '0.8rem', lineHeight: 1.35 }}>
                        <svg width="12" height="12" viewBox="0 0 16 16" fill="#22c55e" style={{ flexShrink: 0 }}>
                          <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                        </svg>
                        <span>{t('upgrade.indexNowFeature')}</span>
                      </div>
                    ) : null}
                    <div className="d-flex align-items-center gap-2" style={{ color: '#9ca3af', fontSize: '0.8rem', lineHeight: 1.35 }}>
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="#e5e7eb" style={{ flexShrink: 0 }}>
                        <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z"/>
                      </svg>
                      <span style={{ textDecoration: 'line-through' }}>{t('upgrade.seoSuiteNotIncluded')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {(showPayment && selectedPlan) && (
          <div className="row g-3 mb-3 justify-content-center">
            <div className="col-12 col-lg-6">
              <div className="card h-100" style={{ borderRadius: '0.75rem', borderWidth: '1px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <div className="card-body p-3">
                  <h5 className="mb-2 fw-bold">{t('upgrade.completePurchase')}</h5>
                  <p className="text-muted small mb-1" style={{ fontSize: '0.8rem' }}>
                    {t('upgrade.proPlanSummary', { products: selectedProducts, price: getCurrentPrice() })}
                  </p>
                  {selectedKeywordPackPlan ? (
                    <p className="text-muted small mb-1" style={{ fontSize: '0.8rem' }}>
                      {t('upgrade.keywordPackLineItem', {
                        count: selectedKeywordPackPlan.no_of_keywords,
                        price: selectedKeywordPackPlan.discounted_price,
                      })}
                    </p>
                  ) : null}
                  <p className="fw-semibold small mb-2" style={{ fontSize: '0.85rem' }}>
                    {t('upgrade.totalDue', { price: getProPlusKeywordTotal() })}
                  </p>
                  {noPaymentOption === true && (
                    <div className="alert alert-danger small mb-2" style={{ fontSize: '0.8rem' }}>
                      {t('upgrade.noPaymentOption')}{' '}
                      {billingUrl && (
                        <a href={billingUrl} target="_blank" rel="noopener noreferrer" className="alert-link">
                          {t('upgrade.ecwidBillingPaymentLink')}
                        </a>
                      )}
                    </div>
                  )}
                  {ecwidChargeError && (
                    <div className="alert alert-danger small mb-2" style={{ fontSize: '0.8rem' }}>
                      {ecwidChargeError}{' '}
                      {billingUrl && (
                        <a href={billingUrl} target="_blank" rel="noopener noreferrer" className="alert-link">
                          {t('upgrade.ecwidBillingPaymentLink')}
                        </a>
                      )}
                    </div>
                  )}
                  <p className="text-muted small mb-2" style={{ fontSize: '0.75rem' }}>{t('upgrade.ecwidBillingHint')}</p>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm w-100"
                    onClick={handleEcwidCharge}
                    disabled={ecwidChargeLoading || noPaymentOption === true}
                  >
                    {ecwidChargeLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        {t('upgrade.processing')}
                      </>
                    ) : (
                      t('upgrade.payWithEcwidButton')
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {(aiDiscoveryUpgradeEnabled && showAidPayment && selectedAidPlan) && (
          <div className="row g-3 mb-3 justify-content-center">
            <div className="col-12 col-lg-6">
              <div className="card h-100" style={{ borderRadius: '0.75rem', borderWidth: '1px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
                <div className="card-body p-3">
                  <h5 className="mb-2 fw-bold">{t('upgrade.completePurchase')}</h5>
                  <p className="text-muted small mb-2" style={{ fontSize: '0.8rem' }}>
                    {t('upgrade.aidPlanSummary', {
                      price: selectedAidPlan.discounted_price || aidPrice,
                      period: aidBillingCycle === 'YEARLY' ? t('upgrade.perYearShort') : t('upgrade.perMonthShort'),
                    })}
                  </p>
                  {aidBillingCycle === 'YEARLY' && parseFloat(aidActualPrice) > parseFloat(aidPrice) ? (
                    <p className="text-muted small mb-2" style={{ fontSize: '0.75rem' }}>
                      <span style={{ textDecoration: 'line-through' }}>${aidActualPrice}</span>
                      {' '}
                      {t('upgrade.savePercent', { pct: aidYearlyDiscountPct })}
                    </p>
                  ) : null}
                  {noPaymentOption === true && (
                    <div className="alert alert-danger small mb-2" style={{ fontSize: '0.8rem' }}>
                      {t('upgrade.noPaymentOption')}{' '}
                      {billingUrl && (
                        <a href={billingUrl} target="_blank" rel="noopener noreferrer" className="alert-link">
                          {t('upgrade.ecwidBillingPaymentLink')}
                        </a>
                      )}
                    </div>
                  )}
                  {ecwidChargeError && (
                    <div className="alert alert-danger small mb-2" style={{ fontSize: '0.8rem' }}>
                      {ecwidChargeError}{' '}
                      {billingUrl && (
                        <a href={billingUrl} target="_blank" rel="noopener noreferrer" className="alert-link">
                          {t('upgrade.ecwidBillingPaymentLink')}
                        </a>
                      )}
                    </div>
                  )}
                  <p className="text-muted small mb-2" style={{ fontSize: '0.75rem' }}>{t('upgrade.ecwidBillingHint')}</p>
                  <button
                    type="button"
                    className="btn btn-dark btn-sm w-100"
                    onClick={handleAidCharge}
                    disabled={aidChargeLoading || noPaymentOption === true}
                  >
                    {aidChargeLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        {t('upgrade.processing')}
                      </>
                    ) : (
                      t('upgrade.payWithEcwidButton')
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Upgrade;
